import { useState, useEffect } from "react";
import MovieCard from "../components/MovieCard";
import { tmdbService } from "../services/tmdbService";

const Movies = () => {
  const [movies, setMovies] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPopularMovies();
  }, []);

  const fetchPopularMovies = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch multiple pages to get more movies (10 pages = 200 movies)
      const pagesToFetch = 10;
      const allMovies = [];
      
      for (let page = 1; page <= pagesToFetch; page++) {
        const data = await tmdbService.getPopularMovies(page);
        const convertedMovies = data.results.map((movie) => ({
          id: `tmdb-${movie.id}`,
          title: movie.title,
          description: movie.overview,
          genre: movie.genre_ids?.join(", ") || "Unknown",
          year: movie.release_date ? parseInt(movie.release_date.split("-")[0]) : 0,
          poster: tmdbService.getImageUrl(movie.poster_path, "w500"),
          rating: Math.min(Math.max(movie.vote_average / 2, 0), 5).toFixed(1),
          trailer: "",
        }));
        allMovies.push(...convertedMovies);
      }
      
      setMovies(allMovies);
    } catch (err) {
      setError("Failed to load movies");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchMovies = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await tmdbService.searchMovies(search);
      const convertedMovies = data.results.map((movie) => ({
        id: `tmdb-${movie.id}`,
        title: movie.title,
        description: movie.overview,
        genre: movie.genre_ids?.join(", ") || "Unknown",
        year: movie.release_date ? parseInt(movie.release_date.split("-")[0]) : 0,
        poster: tmdbService.getImageUrl(movie.poster_path, "w500"),
        rating: Math.min(Math.max(movie.vote_average / 2, 0), 5).toFixed(1),
        trailer: "",
      }));
      setMovies(convertedMovies);
    } catch (err) {
      setError("Search failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = movies.filter((m) => {
    return m.title.toLowerCase().includes(search.toLowerCase());
  });

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const moviesPerPage = 14;
  const maxPages = 10;
  
  const totalPages = Math.min(Math.ceil(filtered.length / moviesPerPage), maxPages);
  const indexOfLastMovie = currentPage * moviesPerPage;
  const indexOfFirstMovie = indexOfLastMovie - moviesPerPage;
  const currentMovies = filtered.slice(indexOfFirstMovie, indexOfLastMovie);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <div className="min-h-screen bg-gray-950 overflow-x-hidden">
      <section className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 py-12 mb-8 relative overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-transparent to-pink-500/10 animate-pulse"></div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '100px 100px'}}></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3 lg:mb-4 leading-tight">
              <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                All Movies
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-xl mx-auto leading-relaxed">Browse our collection of popular movies</p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 mb-8">
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl shadow-lg p-6 border border-white/10">
          {/* Search Form - Centered */}
          <div className="max-w-xl mx-auto text-center">
            <label className="block text-sm font-medium text-gray-300 mb-2">Search Movies</label>
            <form onSubmit={handleSearchMovies} className="relative flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <input 
                  type="text" 
                  placeholder="Search by title..." 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-white placeholder-gray-400"
                />
              </div>
              <button 
                type="submit" 
                disabled={isLoading || !search.trim()} 
                className="px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {isLoading ? "..." : "Search"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 pb-16">
        {isLoading && (
          <div className="text-center py-16">
            <p className="text-xl text-gray-400">Loading movies...</p>
          </div>
        )}
        {error && !isLoading && (
          <div className="text-center py-16">
            <h3 className="text-2xl font-bold text-red-400 mb-2">{error}</h3>
            <button onClick={fetchPopularMovies} className="px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 whitespace-nowrap">Try Again</button>
          </div>
        )}
        {!isLoading && !error && currentMovies.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {currentMovies.map((movie, index) => (
                <div key={movie.id} className="animate-slide-up" style={{animationDelay: `${index * 50}ms`}}>
                  <MovieCard movie={movie} />
                </div>
              ))}
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="mt-12 flex items-center justify-center space-x-2">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white/10 text-gray-300 hover:bg-white/20"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-orange-500 text-white shadow-md'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/10'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white/10 text-gray-300 hover:bg-white/20"
                >
                  Next
                </button>
              </div>
            )}

            {/* Page Info */}
            <div className="mt-4 text-center text-sm text-gray-500">
              Showing {indexOfFirstMovie + 1}-{Math.min(indexOfLastMovie, filtered.length)} of {filtered.length} movies
              {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
            </div>
          </>
        ) : !isLoading && !error && (
          <div className="text-center py-16">
            <h3 className="text-2xl font-bold text-white mb-2">No movies found</h3>
            <p className="text-gray-400 mb-6">{search ? `No movies match "${search}". Try a different search term.` : "No movies available. Try refreshing the page!"}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {search && (<button onClick={() => { setSearch(""); fetchPopularMovies(); }} className="px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors">Clear Search</button>)}
              <button onClick={fetchPopularMovies} className="px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors">Load Popular Movies</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Movies;
